import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { hasStoredAccessToken } from "@/shared/utils/auth";
import { useMembershipStatus } from "@/features/plan/services";
import { useProfile } from "@/features/profile/services";
import { isApiError } from "@/shared/api";
import { resolveRouteAfterPaymentSuccess } from "../lib/after-payment-flow";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = hasStoredAccessToken();
  const sessionId = searchParams.get("session_id");

  const membershipQuery = useMembershipStatus({
    enabled: isAuthenticated,
    retry: 1,
    refetchInterval: (query) => {
      const status =
        (
          query.state.data as { status?: string } | undefined
        )?.status?.toUpperCase() ?? "";
      return status === "PENDING" ? 2000 : false;
    },
  });

  const profileQuery = useProfile({
    enabled: isAuthenticated,
    retry: 1,
    refetchInterval:
      membershipQuery.data?.status?.toUpperCase() === "PENDING" ? 2000 : false,
  });
  const membershipNotFound =
    isApiError(membershipQuery.error) &&
    (membershipQuery.error.errorName === "membership-not-found" ||
      membershipQuery.error.errorCode === "404103");

  const targetRoute = isAuthenticated
    ? membershipNotFound
      ? "/plan/membership"
      : resolveRouteAfterPaymentSuccess({
          membershipStatus: membershipQuery.data?.status,
          kycStatus: profileQuery.data?.kyc?.status,
          kycAttemptStatus: profileQuery.data?.kyc?.attemptStatus,
        })
    : null;

  useEffect(() => {
    if (!isAuthenticated || !targetRoute) {
      return;
    }

    const redirectTimer = window.setTimeout(() => {
      navigate(targetRoute, { replace: true });
    }, 1200);

    return () => window.clearTimeout(redirectTimer);
  }, [isAuthenticated, navigate, targetRoute]);

  if (!isAuthenticated) {
    const returnPath = `/payment/success${window.location.search}`;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-main-dark px-6 text-center text-white">
        <h1 className="text-2xl font-medium">Payment received</h1>
        <p className="max-w-sm text-sm text-white/70">
          Sign in to finish your membership setup and continue the onboarding
          flow.
        </p>
        {sessionId ? (
          <p className="text-xs text-white/40">Session: {sessionId}</p>
        ) : null}
        <Button
          onClick={() =>
            navigate("/sign-in", {
              replace: true,
              state: { from: returnPath },
            })
          }>
          Sign in
        </Button>
      </div>
    );
  }

  if (membershipQuery.isLoading || profileQuery.isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-main-dark px-6 text-center text-white">
        <Spinner className="size-8" />
        <p className="text-sm text-white/70">
          Finalizing your payment and membership...
        </p>
      </div>
    );
  }

  if (
    (membershipQuery.isError && !membershipNotFound) ||
    profileQuery.isError
  ) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-main-dark px-6 text-center text-white">
        <h1 className="text-2xl font-medium">
          We couldn't confirm your payment
        </h1>
        <p className="max-w-sm text-sm text-white/70">
          Your payment may still have gone through. Retry to refresh your
          membership status.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            onClick={() => {
              void membershipQuery.refetch();
              void profileQuery.refetch();
            }}>
            Retry
          </Button>
          <Button
            variant="outline"
            className="border-white/30 text-white"
            onClick={() => navigate("/plan/membership", { replace: true })}>
            Membership Page
          </Button>
        </div>
      </div>
    );
  }

  if (!targetRoute) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-main-dark px-6 text-center text-white">
        <Spinner className="size-8" />
        <h1 className="text-2xl font-medium">Payment completed</h1>
        <p className="max-w-sm text-sm text-white/70">
          We are waiting for the final membership confirmation. This page will
          refresh automatically.
        </p>
        {sessionId ? (
          <p className="text-xs text-white/40">Session: {sessionId}</p>
        ) : null}
      </div>
    );
  }

  const redirectLabel =
    targetRoute === "/dashboard"
      ? "Redirecting you to the dashboard..."
      : "Redirecting you to the next setup step...";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-main-dark px-6 text-center text-white">
      <Spinner className="size-8" />
      <h1 className="text-2xl font-medium">Payment completed</h1>
      <p className="max-w-sm text-sm text-white/70">{redirectLabel}</p>
      {sessionId ? (
        <p className="text-xs text-white/40">Session: {sessionId}</p>
      ) : null}
    </div>
  );
}
